import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo
} from 'react';
import { useAuth } from 'react-oidc-context';
import { roleAPI } from 'services/swagger-api';
import { signinSilentSingleFlight } from 'features/auth/singleFlightSilentRenew';
import {
  GetCurrentRoleResponse,
  GetPortailsResponse,
  PortailMinimalDto,
  RoleMinimalDto
} from 'api/role';
import {
  Capability,
  LEGACY_EMPTY_SESSION,
  RoleSession,
  isSameRoleName,
  menuItemVisibleForSession,
  routeAllowedForSession,
  sessionCan,
  RoleNames
} from 'core/roles';

// Toute la logique « qui voit/fait quoi » vit dans le manager central core/roles
// (session double mode : portail + briques, ou rôle actif legacy).
// Ici : uniquement le chargement de la session et les switchs.

interface RoleContextProps {
  /** La session courante (mode portail ou legacy) — source des politiques UI. */
  session: RoleSession;
  /** Mode legacy uniquement (null en mode portail). */
  currentRole: RoleMinimalDto | null;
  roles: RoleMinimalDto[];
  /** Mode portail uniquement. */
  portailActuel: PortailMinimalDto | null;
  portails: PortailMinimalDto[];
  isLoading: boolean;
  /** Gate de capacité par la session (matrice centrale core/roles). */
  can: (capability: Capability) => boolean;
  isRouteAllowed: (path: string) => boolean;
  isMenuItemAllowed: (itemId: string) => boolean;
  /** Switch du rôle actif legacy — par NOM (contrat back : body { roleName }). */
  switchRole: (roleName: string) => Promise<void>;
  /** Switch du portail actif — par NOM (contrat back : body { portail }). */
  switchPortail: (portail: string) => Promise<void>;
  refetchRoles: () => Promise<void>;
}

const RoleContext = createContext<RoleContextProps | undefined>(undefined);

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const auth = useAuth();
  const [roleData, setRoleData] = useState<GetCurrentRoleResponse | null>(null);
  const [portailData, setPortailData] = useState<GetPortailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadRoles = useCallback(async () => {
    if (!auth.isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      // Double mode (refacto rôles) : les portails d'abord. Un compte migré aux
      // briques vit en mode portail ; sinon on retombe sur le sélecteur de rôles
      // legacy (admin, gestionnaires, comptes non migrés).
      const portails = await roleAPI.getPortails();
      setPortailData(portails);

      if (portails.estModePortail) {
        setRoleData(null);
        return;
      }

      const response = await roleAPI.getCurrentRole();
      setRoleData(response);
    } catch (err) {
      console.error('Erreur lors du chargement des roles:', err);
    } finally {
      setIsLoading(false);
    }
  }, [auth.isAuthenticated]);

  useEffect(() => {
    // Charger les rôles seulement quand l'utilisateur est authentifié
    if (auth.isAuthenticated && auth.user) {
      loadRoles();
    } else if (!auth.isAuthenticated) {
      setIsLoading(false);
      setRoleData(null);
      setPortailData(null);
    }
  }, [auth.isAuthenticated, auth.user, loadRoles]);

  const estModePortail = portailData?.estModePortail === true;

  const currentRole = useMemo(
    () => roleData?.currentRole || null,
    [roleData?.currentRole]
  );

  const roles = useMemo(() => roleData?.roles || [], [roleData?.roles]);

  const portailActuel = useMemo(
    () => (estModePortail ? portailData?.portailActuel || null : null),
    [estModePortail, portailData?.portailActuel]
  );

  const portails = useMemo(
    () => (estModePortail ? portailData?.portails || [] : []),
    [estModePortail, portailData?.portails]
  );

  const session = useMemo<RoleSession>(() => {
    if (estModePortail) {
      return {
        mode: 'portail',
        portail: portailActuel?.nom ?? null,
        briques: portailActuel?.briques ?? []
      };
    }

    return currentRole
      ? { mode: 'legacy', roleName: currentRole.name, briques: [] }
      : LEGACY_EMPTY_SESSION;
  }, [estModePortail, portailActuel, currentRole]);

  // Délégation au manager central (politiques par session).
  const can = useCallback(
    (capability: Capability): boolean => sessionCan(session, capability),
    [session]
  );

  const isRouteAllowed = useCallback(
    (path: string): boolean => routeAllowedForSession(session, path),
    [session]
  );

  const isMenuItemAllowed = useCallback(
    (itemId: string): boolean => menuItemVisibleForSession(session, itemId),
    [session]
  );

  const clearAppStorage = useCallback(() => {
    // Préserver les clés OIDC et la sauvegarde admin d'une impersonation en cours
    // (sinon « Revenir à mon compte » la perd après un changement de contexte)
    const oidcKeyPatterns = ['oidc.', 'EspacePro_App', 'impersonation.'];

    const isOidcKey = (key: string) =>
      oidcKeyPatterns.some((pattern) => key.includes(pattern));

    // Nettoyer localStorage (sauf OIDC)
    const localStorageKeys = Object.keys(localStorage);
    localStorageKeys.forEach((key) => {
      if (!isOidcKey(key)) {
        localStorage.removeItem(key);
      }
    });

    // Nettoyer sessionStorage (sauf OIDC)
    const sessionStorageKeys = Object.keys(sessionStorage);
    sessionStorageKeys.forEach((key) => {
      if (!isOidcKey(key)) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  /**
   * Séquence commune aux deux switchs : refresh silencieux du token (réaligne les
   * claims — le serveur, lui, applique déjà le nouveau contexte), nettoyage du
   * storage applicatif, rechargement complet sur la landing cible.
   */
  const finalizeSwitch = useCallback(
    async (landing: string) => {
      try {
        // Single-flight : un seul renew concurrent (rafales de tokens observées en prod)
        await signinSilentSingleFlight(auth);
      } catch (renewErr) {
        console.error('Silent renew échoué après switch:', renewErr);
      }

      clearAppStorage();
      window.location.href = landing;
    },
    [auth, clearAppStorage]
  );

  const switchRole = useCallback(
    async (roleName: string) => {
      try {
        // PATCH { roleName } : le back persiste le rôle actif (enforcé serveur à
        // chaque requête, effet immédiat même avant le renew).
        const response = await roleAPI.switchCurrentRole({
          switchRoleCommand: { roleName }
        });
        setRoleData(response);

        // Landing réévaluée selon la casquette cible (admin → back-office).
        await finalizeSwitch(
          isSameRoleName(roleName, RoleNames.Admin) ? '/back-office/home' : '/bord'
        );
      } catch (err) {
        console.error('Erreur lors du changement de role:', err);
      }
    },
    [finalizeSwitch]
  );

  const switchPortail = useCallback(
    async (portail: string) => {
      try {
        const response = await roleAPI.switchCurrentPortail({
          switchPortailCommand: { portail }
        });
        setPortailData(response);
        await finalizeSwitch('/bord');
      } catch (err) {
        console.error('Erreur lors du changement de portail:', err);
      }
    },
    [finalizeSwitch]
  );

  const value = useMemo(
    () => ({
      session,
      currentRole,
      roles,
      portailActuel,
      portails,
      isLoading,
      can,
      isRouteAllowed,
      isMenuItemAllowed,
      switchRole,
      switchPortail,
      refetchRoles: loadRoles
    }),
    [
      session,
      currentRole,
      roles,
      portailActuel,
      portails,
      isLoading,
      can,
      isRouteAllowed,
      isMenuItemAllowed,
      switchRole,
      switchPortail,
      loadRoles
    ]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = (): RoleContextProps => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};

// NB : pour gater l'UI, utiliser `useRoleCan(Capability.X)` (délègue à `can`) —
// la matrice double mode (rôles legacy / briques) vit dans core/roles.
